import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Leaf,
  Truck,
  Users,
  Building2,
  Globe2,
  ShieldCheck,
  ChefHat,
  Info,
  Network,
} from 'lucide-react';

const ASSETS_DIR = '/assets/img/panda/delivery-areas';

const COUNTRIES = [
  {
    code: 'us',
    image: `${ASSETS_DIR}/country-us.png`,
    name: 'United States',
    points: [
      'Major cities and surrounding areas',
      'Selected metropolitan areas',
      'Coverage varies by delivery address',
    ],
  },
  {
    code: 'ca',
    image: `${ASSETS_DIR}/country-ca.png`,
    name: 'Canada',
    points: [
      'Major cities and surrounding areas',
      'Selected metropolitan areas',
      'Coverage varies by delivery address',
    ],
  },
  {
    code: 'uk',
    image: `${ASSETS_DIR}/country-uk.png`,
    name: 'United Kingdom',
    points: [
      'Major cities and surrounding areas',
      'Selected metropolitan areas',
      'Coverage varies by delivery address',
    ],
  },
];

const COUNTRY_ICONS = [MapPin, Building2, Globe2];

const COVERAGE = [
  {
    icon: Building2,
    title: 'Major Cities',
    text: 'We deliver to major cities across each country.',
  },
  {
    icon: MapPin,
    title: 'Surrounding Areas',
    text: 'We also cover surrounding areas near major cities.',
  },
  {
    icon: Building2,
    title: 'Business Districts',
    text: 'Available in many business districts and commercial areas.',
  },
];

const HIGHLIGHTS = [
  { icon: MapPin, title: 'Wide Coverage', text: 'Across the US, Canada and the UK' },
  { icon: Truck, title: 'Reliable Delivery', text: 'Fresh and on time' },
  { icon: ChefHat, title: 'Great Food', text: 'Meals teams love' },
  { icon: Users, title: 'For Every Team', text: 'Small teams to large enterprises' },
  { icon: Network, title: 'Growing Network', text: 'Expanding to more areas' },
  { icon: ShieldCheck, title: 'Trusted Service', text: 'Safe, reliable and professional' },
];

const DeliveryAreas = () => {
  const [query, setQuery] = useState('');

  return (
    <main className="bg-[#FDF6EE] pt-[88px]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] sm:text-xs font-bold text-[#E53935] uppercase tracking-wider mb-2">
              Delivery Areas
            </p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#202124] leading-tight">
              Good food,
              <br />
              wherever your <span className="text-[#E53935]">team works.</span>
            </h1>
            <p className="mt-4 text-gray-500 text-sm sm:text-base max-w-xl">
              We deliver fresh workplace meals across major cities and
              surrounding areas in the United States, Canada, and the United
              Kingdom.
            </p>

            <form
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg"
              onSubmit={e => e.preventDefault()}
            >
              <div className="relative flex-1">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 bg-white text-sm text-[#202124] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E53935]/30 focus:border-[#E53935]"
                  placeholder="Enter your city or ZIP/postal code"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  aria-label="Enter your city or ZIP/postal code"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3.5 rounded-xl bg-[#E53935] text-white text-sm font-heading font-bold hover:bg-[#d32f2f] transition-colors whitespace-nowrap"
              >
                Check Availability →
              </button>
            </form>

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Leaf className="w-5 h-5 text-[#E53935]" />
                Fresh, High-Quality Meals
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Truck className="w-5 h-5 text-[#E53935]" />
                Reliable Delivery
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-5 h-5 text-[#E53935]" />
                For Teams of Any Size
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-gray-200/80 aspect-[4/3] flex items-center justify-center">
              <img
                src={`${ASSETS_DIR}/hero-skyline.png`}
                alt="Panda On Wheels delivery van across major cities"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 text-center">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#202124]">
          Where We Deliver
        </h2>
        <p className="mt-3 text-gray-500 text-sm sm:text-base max-w-2xl mx-auto">
          Panda On Wheels is available in the United States, Canada, and the
          United Kingdom. Coverage includes major cities and surrounding
          areas.
        </p>

        <img
          src={`${ASSETS_DIR}/route-map.png`}
          alt="Panda On Wheels delivery route map across countries"
          className="mt-8 rounded-2xl border border-gray-200/80 w-full h-64 sm:h-80 object-cover"
        />

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
          {COUNTRIES.map(country => (
            <div
              key={country.code}
              className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
            >
              <div className="h-32 overflow-hidden">
                <img
                  src={country.image}
                  alt={country.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold text-base text-[#202124] mb-3">
                  {country.name}
                </h3>
                <ul className="space-y-2 mb-4">
                  {country.points.map((point, index) => {
                    const Icon = COUNTRY_ICONS[index];
                    return (
                      <li
                        key={point}
                        className="flex items-center gap-2 text-xs text-gray-500"
                      >
                        <Icon className="w-3.5 h-3.5 text-[#E53935] flex-shrink-0" />
                        {point}
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to="/delivery-areas"
                  className="text-xs font-bold text-[#E53935] hover:underline"
                >
                  Check Availability →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-14">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 items-stretch">
          <div className="sm:col-span-8 rounded-3xl overflow-hidden border border-gray-200/80">
            <img
              src={`${ASSETS_DIR}/van.png`}
              alt="Panda On Wheels delivery van"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="sm:col-span-4 flex">
            <div className="bg-panda-tint rounded-3xl p-6 text-center flex flex-col items-center justify-center w-full">
              <span className="inline-flex w-12 h-12 rounded-full bg-white items-center justify-center mb-3">
                <Globe2 className="w-6 h-6 text-[#E53935]" />
              </span>
              <h3 className="font-heading font-bold text-base text-[#202124] mb-1">
                Three Countries
                <br />
                One Mission
              </h3>
              <p className="text-xs text-gray-500">
                Fresh meals. Happy teams. Stronger workplaces.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8 text-center">
        <img
          src={`${ASSETS_DIR}/food-bowl.png`}
          alt=""
          className="w-16 h-16 rounded-full object-cover shadow-md border-4 border-white mx-auto mb-4"
        />
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#202124]">
          Coverage That Fits Your Location
        </h2>
        <p className="mt-3 text-gray-500 text-sm sm:text-base max-w-2xl mx-auto">
          We aim to make great food accessible to more teams. Our delivery
          coverage includes:
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
          {COVERAGE.map(item => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="bg-white rounded-2xl border border-gray-200/80 p-6"
              >
                <span className="inline-flex w-11 h-11 rounded-xl bg-panda-tint items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#E53935]" />
                </span>
                <h3 className="font-heading font-bold text-base text-[#202124] mb-1">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500">{item.text}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3 bg-panda-tint/60 rounded-xl px-5 py-4 text-left">
          <Info className="w-5 h-5 text-[#E53935] flex-shrink-0" />
          <p className="text-sm text-gray-600">
            Availability depends on the delivery address. Enter your location
            above to check coverage.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">
          {HIGHLIGHTS.map(item => {
            const Icon = item.icon;
            return (
              <div key={item.title}>
                <span className="inline-flex w-11 h-11 rounded-full bg-panda-tint items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-[#E53935]" />
                </span>
                <h3 className="font-heading font-bold text-sm text-[#202124] mb-1">
                  {item.title}
                </h3>
                <p className="text-xs text-gray-500">{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default DeliveryAreas;
